import { createFileRoute } from '@tanstack/react-router';
import { Button, Stack } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  recoverManagerPasswordDocument,
  resendManagerVerificationEmailDocument,
} from '@social-garden/api/documents/manager.ts';
import {
  RecoverPasswordFieldValues,
  RecoverPasswordSchema,
} from '@/constants/ValidationSchema.ts';
import CodeField from '@/components/CodeField.tsx';
import NewPasswordField from '@/components/NewPasswordField.tsx';
import ConfirmPasswordField from '@/components/ConfirmPasswordField.tsx';
import ReadOnlyEmailField from '@/components/ReadOnlyEmailField.tsx';
import { z } from 'zod';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';
import PageTitle from '@/components/PageTitle.tsx';

const RecoverPasswordSearchSchema = z.object({
  email: z.string(),
  code: z.string().optional(),
});

export const Route = createFileRoute('/_guest/recover-password')({
  component: RecoverPassword,
  validateSearch: (search) => RecoverPasswordSearchSchema.parse(search),
});

function RecoverPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation(['manager']);
  const searchParams = Route.useSearch();
  const [recoverManagerPassword, { loading: recoverManagerPasswordLoading }] =
    useMutation(recoverManagerPasswordDocument);
  const [
    resendManagerVerificationEmail,
    { loading: resendManagerVerificationEmailLoading },
  ] = useMutation(resendManagerVerificationEmailDocument);

  const {
    control,
    formState: { dirtyFields, isValid, isSubmitting },
    handleSubmit,
  } = useForm<RecoverPasswordFieldValues>({
    resolver: zodResolver(RecoverPasswordSchema),
    mode: 'all',
    defaultValues: {
      email: searchParams.email,
      code: searchParams.code,
    },
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting });

  const handleOnResendCode = useCallback(async () => {
    await resendManagerVerificationEmail({
      variables: {
        input: {
          email: searchParams.email,
        },
      },
    });
  }, [searchParams.email, resendManagerVerificationEmail]);

  const handleOnValid = useCallback(
    async ({ email, code, password }: RecoverPasswordFieldValues) => {
      const result = await recoverManagerPassword({
        variables: {
          input: {
            email,
            code,
            password,
          },
        },
      });
      if (result.data?.recoverManagerPassword) {
        await navigate({ to: '/login' });
      }
    },
    [recoverManagerPassword, navigate],
  );

  return (
    <Stack spacing={4}>
      <Stack spacing={1} alignItems="center">
        <PageTitle variant="h4" textAlign="center">
          {t('recoverPassword.header')}
        </PageTitle>
      </Stack>
      <Stack
        component="form"
        spacing={2}
        noValidate
        onSubmit={handleSubmit(handleOnValid)}>
        <Controller<RecoverPasswordFieldValues, 'email'>
          name="email"
          control={control}
          render={ReadOnlyEmailField}
        />
        <Controller<RecoverPasswordFieldValues, 'code'>
          name="code"
          control={control}
          render={CodeField}
        />
        <Controller<RecoverPasswordFieldValues, 'password'>
          name="password"
          control={control}
          render={NewPasswordField}
        />
        <Controller<RecoverPasswordFieldValues, 'passwordConfirm'>
          name="passwordConfirm"
          control={control}
          render={ConfirmPasswordField}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          loading={recoverManagerPasswordLoading}
          disabled={!isValid}>
          {t('recoverPassword.submitButton')}
        </Button>
        <Button
          variant="outlined"
          loading={resendManagerVerificationEmailLoading}
          onClick={handleOnResendCode}>
          {t('recoverPassword.resendCodeButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
